/**
 * Device fingerprint collector for the RPD reader.
 *
 * React port of the fingerprint script that runs in the RDocs MVC reader
 * (the one that POSTs to /Documents/SendDFPD). Same attribute set, but the
 * defects in the original are fixed here - see docs/device-fingerprint.md
 * for the full list.
 *
 * Contract:
 *   - Never throws. Every probe is individually guarded; a probe that fails
 *     simply leaves its attributes out of the result.
 *   - Always settles. Every asynchronous probe is bounded by a timeout, so a
 *     browser that never fires `onvoiceschanged` (Chrome, first call) or
 *     never answers a Worker cannot hang authentication.
 *   - Leaves nothing behind. AudioContexts are closed, blob URLs revoked,
 *     probe DOM nodes removed, no listeners registered.
 *   - Emits no raw images. Canvas/WebGL renders are hashed client side; the
 *     data URLs never leave the browser, which keeps the payload ~4 KB
 *     instead of ~120 KB.
 *
 * Sent on the reader's POST /docs/verify/{id} calls only (handled by
 * PostVerifyUser), inside the existing credentials body. The API logs it to
 * NLog and nothing else - it is not stored and no verification rule reads
 * it.
 *
 * Result shape (what gets posted as `deviceFingerprint`):
 *   {
 *     visitorId, version, collectedOn, collectionMs, confidence,
 *     automation: { webdriver, cdpAutomation, ... },
 *     attributes: { <identifying signals, all strings> },
 *     notes:      { <human readable explanations, not identifying> }
 *   }
 */

export const FP_VERSION = "1.0";

const WORKER_TIMEOUT_MS = 1200;
const VOICES_TIMEOUT_MS = 800;
const AUDIO_TIMEOUT_MS = 1500;
const GPU_TIMEOUT_MS = 1000;

/**
 * Attributes that describe the machine rather than the moment.
 *
 * Only these feed the visitorId. Everything else (window size, browser
 * version, audio latency, DST offset) is still logged, but a change in one
 * of them must not produce a different device id for the same machine.
 */
export const STABLE_KEYS = [
  // GPU / rendering pipeline
  "gpuVendor",
  "gpuRenderer",
  "webglVendor",
  "webglRenderer",
  "webglMaxTextureSize",
  "webglMaxViewportDimensions",
  "webglAliasedLineWidthRange",
  "webglAliasedPointSizeRange",
  "webglExtensionsCount",
  "webglHash",
  "webglCanvasHash",
  "canvas2dHash",
  // Audio pipeline
  "audioHash",
  "ac_sampleRate",
  "ac_channelCount",
  // OS / hardware
  "cpuCores",
  "deviceMemory",
  "screenResolution",
  "colorDepth",
  "colorGamut",
  "screenTouchPoints",
  "anyPointer",
  "anyHover",
  "deviceInfoPlatform",
  "deviceInfoArchitecture",
  "deviceInfoBitness",
  // Installed software surface
  "fontList",
  "speechVoiceHash",
  "keyboardlayoutSize",
  "audioCodecs",
  "videoCodecs",
  // Locale
  "timezoneName",
  "mainLanguage",
  "languages",
];

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

function s(value, fallback = "") {
  try {
    if (value === null || value === undefined) return fallback;
    return String(value);
  } catch (e) {
    return fallback;
  }
}

/** Resolves to `fallback` if `promise` has not settled within `ms`. */
function withTimeout(promise, ms, fallback) {
  let timer;
  const guard = new Promise((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise, guard]).then(
    (value) => {
      clearTimeout(timer);
      return value;
    },
    () => {
      clearTimeout(timer);
      return fallback;
    }
  );
}

/** FNV-1a 32 bit. Synchronous fallback when SubtleCrypto is unavailable. */
export function fnv1a(str) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * SHA-256 hex. Falls back to a doubled FNV-1a when SubtleCrypto is missing,
 * which is the case on plain-HTTP origins - the reader is always HTTPS in
 * production, so that path is only hit in local development.
 */
export async function sha256Hex(str) {
  try {
    const subtle = window.crypto && (window.crypto.subtle || window.crypto.webkitSubtle);
    if (subtle && typeof TextEncoder !== "undefined") {
      const bytes = new TextEncoder().encode(str);
      const digest = await subtle.digest("SHA-256", bytes);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch (e) {
    /* fall through */
  }
  return fnv1a(str) + fnv1a(str.split("").reverse().join(""));
}

/* ------------------------------------------------------------------ */
/* probes                                                              */
/* ------------------------------------------------------------------ */

/**
 * Audio stack.
 *
 * The original read `getByteFrequencyData` off an analyser that was never
 * connected to a source, which is always all zeros, and then discarded the
 * result and hardcoded "124.04". This renders a fixed signal through an
 * OfflineAudioContext instead: the DynamicsCompressor output differs
 * measurably between CPU/OS/browser audio stacks and is deterministic on the
 * same machine, which is what makes it usable as a signal.
 */
async function collectAudio(attrs) {
  // Live context - property readout only. Never resumed, so no autoplay
  // warning, and closed immediately so we do not burn one of the ~6
  // AudioContexts a page is allowed.
  let live = null;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) {
      live = new AC();
      const analyser = live.createAnalyser();
      analyser.fftSize = 2048;
      analyser.minDecibels = -100;
      analyser.maxDecibels = -30;
      analyser.smoothingTimeConstant = 0.8;

      attrs.ac_baseLatency = s(
        typeof live.baseLatency === "number" ? live.baseLatency.toFixed(3) : ""
      );
      attrs.ac_outputLatency = s(live.outputLatency, "N/A");
      attrs.ac_sinkId = s(live.sinkId, "Default");
      attrs.ac_sampleRate = s(live.sampleRate);
      attrs.ac_numberOfInputs = s(live.destination.numberOfInputs);
      attrs.ac_numberOfOutputs = s(live.destination.numberOfOutputs);
      attrs.ac_channelCount = s(live.destination.channelCount);
      attrs.ac_channelCountMode = s(live.destination.channelCountMode);
      attrs.ac_channelInterpretation = s(live.destination.channelInterpretation);

      attrs.an_fftSize = s(analyser.fftSize);
      attrs.an_frequencyBinCount = s(analyser.frequencyBinCount);
      attrs.an_minDecibels = s(analyser.minDecibels);
      attrs.an_maxDecibels = s(analyser.maxDecibels);
      attrs.an_smoothingTimeConstant = s(analyser.smoothingTimeConstant);
      attrs.an_numberOfInputs = s(analyser.numberOfInputs);
      attrs.an_numberOfOutputs = s(analyser.numberOfOutputs);
      attrs.an_channelCount = s(analyser.channelCount);
      attrs.an_channelCountMode = s(analyser.channelCountMode);
      attrs.an_channelInterpretation = s(analyser.channelInterpretation);
    }
  } catch (e) {
    /* audio unavailable */
  } finally {
    try {
      if (live && typeof live.close === "function" && live.state !== "closed") {
        live.close();
      }
    } catch (e) {
      /* ignore */
    }
  }

  // Offline render - the actual fingerprint.
  try {
    const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!OAC) return;

    const rendered = await withTimeout(
      new Promise((resolve, reject) => {
        try {
          const ctx = new OAC(1, 44100, 44100);
          const oscillator = ctx.createOscillator();
          oscillator.type = "triangle";
          oscillator.frequency.setValueAtTime(10000, ctx.currentTime);

          const compressor = ctx.createDynamicsCompressor();
          compressor.threshold.setValueAtTime(-50, ctx.currentTime);
          compressor.knee.setValueAtTime(40, ctx.currentTime);
          compressor.ratio.setValueAtTime(12, ctx.currentTime);
          compressor.attack.setValueAtTime(0, ctx.currentTime);
          compressor.release.setValueAtTime(0.25, ctx.currentTime);

          oscillator.connect(compressor);
          compressor.connect(ctx.destination);
          oscillator.start(0);

          // Older Safari resolves through the event, not the promise.
          ctx.oncomplete = (event) => resolve(event.renderedBuffer);
          const maybePromise = ctx.startRendering();
          if (maybePromise && typeof maybePromise.then === "function") {
            maybePromise.then(resolve, reject);
          }
        } catch (err) {
          reject(err);
        }
      }),
      AUDIO_TIMEOUT_MS,
      null
    );

    if (!rendered) return;

    const channel = rendered.getChannelData(0);
    let sum = 0;
    for (let i = 4500; i < 5000 && i < channel.length; i++) {
      sum += Math.abs(channel[i]);
    }
    attrs.audioSample = sum.toFixed(8);
    attrs.audioHash = fnv1a(attrs.audioSample);
    // Kept under the original name so rows from the MVC reader and rows from
    // this reader line up in the same report.
    attrs.pxi_output = attrs.audioSample;
  } catch (e) {
    /* offline audio unavailable */
  }
}

/** User-Agent Client Hints: bitness, brands, form factors, platform. */
async function collectClientHints(attrs, notes) {
  try {
    attrs.browserUseragent = s(navigator.userAgent);

    const uaData = navigator.userAgentData;
    if (!uaData || typeof uaData.getHighEntropyValues !== "function") {
      attrs.bitness = "Not supported";
      attrs.browserBrands = "";
      attrs.secChUaFormFactors = "";
      notes.bitness = "Your browser does not allow high-entropy client hints.";
      return;
    }

    try {
      attrs.browserBrands = JSON.stringify(uaData.brands || []);
    } catch (e) {
      attrs.browserBrands = "";
    }

    const values = await withTimeout(
      uaData.getHighEntropyValues([
        "architecture",
        "bitness",
        "platform",
        "platformVersion",
        "model",
        "formFactors",
      ]),
      GPU_TIMEOUT_MS,
      null
    );

    if (!values) {
      attrs.bitness = "Not supported";
      notes.bitness = "High-entropy client hints did not resolve in time.";
      return;
    }

    attrs.bitness = s(values.bitness);
    attrs.secChUaFormFactors = JSON.stringify(values.formFactors || []);

    let explanation = `The browser reported ${values.bitness}-bit architecture. `;
    if (values.platform === "Windows") {
      explanation += "On Windows, 64 means 64-bit OS & browser. 32 means 32-bit browser.";
    } else if (values.platform === "Android") {
      explanation += "On Android, bitness usually reflects CPU architecture (ARM64 vs ARM32).";
    } else if (values.platform === "Linux" || values.platform === "Chrome OS") {
      explanation += "On Linux/ChromeOS, bitness depends on CPU architecture.";
    }
    explanation += ` Detected architecture: ${values.architecture}, platform: ${values.platform}.`;
    notes.bitness = explanation;
  } catch (e) {
    attrs.bitness = "Not supported";
    notes.bitness = "Your browser does not allow high-entropy client hints.";
  }
}

/** Codec support matrix - reflects the media stack the OS exposes. */
function collectCodecs(attrs) {
  try {
    const audio = document.createElement("audio");
    attrs.audioCodecs = ["audio/mpeg", "audio/ogg", "audio/wav", "audio/mp4", "audio/webm"]
      .map((codec) => codec + ":" + audio.canPlayType(codec))
      .join("|");
  } catch (e) {
    attrs.audioCodecs = "";
  }

  try {
    const video = document.createElement("video");
    attrs.videoCodecs = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      'video/mp4; codecs="avc1.42E01E"',
      'video/webm; codecs="vp9"',
    ]
      .map((codec) => codec + ":" + video.canPlayType(codec))
      .join("|");
  } catch (e) {
    attrs.videoCodecs = "";
  }
}

/**
 * Automation / tampering signals.
 *
 * These are advisory, not proof. The `error.stack` getter trick fires for
 * anyone with DevTools open, not only for a CDP driven browser, and the
 * double-toDataURL check only catches spoofers that randomise per read - a
 * spoofer returning a fixed fake canvas passes it. Treat them as inputs to a
 * risk score, never as a hard block on their own.
 */
function collectAutomation(automation) {
  try {
    automation.webdriver = s(navigator.webdriver === true);
  } catch (e) {
    automation.webdriver = "";
  }

  try {
    let detected = false;
    const err = new Error();
    Object.defineProperty(err, "stack", {
      configurable: true,
      get() {
        detected = true;
        return "";
      },
    });
    // eslint-disable-next-line no-console
    console.debug(err);
    automation.cdpAutomation = s(detected);
  } catch (e) {
    automation.cdpAutomation = "Unknown";
  }

  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "16px Arial";
    ctx.fillText("RPostCanvasFingerprint", 2, 2);
    automation.hasModifiedCanvasFingerprint = s(canvas.toDataURL() !== canvas.toDataURL());
  } catch (e) {
    automation.hasModifiedCanvasFingerprint = "Unknown";
  }

  // navigator.webdriver read from a fresh realm - some patched drivers only
  // override it on the top-level window.
  let iframe = null;
  try {
    iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
    automation.webdriverIFrame = s(iframe.contentWindow.navigator.webdriver === true);
  } catch (e) {
    automation.webdriverIFrame = "";
  } finally {
    try {
      if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    } catch (e) {
      /* ignore */
    }
  }
}

/**
 * Worker realm readout.
 *
 * One Worker for all four probes instead of five separate blob Workers, and
 * awaited rather than fire-and-forget - in the original, `worker.onmessage`
 * was assigned but never waited on, so the payload was posted before any
 * Worker had answered and these fields were always empty.
 *
 * The point of reading them in a Worker at all: a spoofing extension that
 * patches `navigator` on the window often forgets the Worker realm, so a
 * mismatch between the two is itself a signal.
 */
async function collectWorker(attrs, automation) {
  const source = `
    self.onmessage = function () {
      var cdp = false;
      try {
        var err = new Error();
        Object.defineProperty(err, 'stack', {
          configurable: true,
          get: function () { cdp = true; return ''; }
        });
        console.debug(err);
      } catch (e) {}

      self.postMessage({
        platform: (self.navigator && self.navigator.platform) || '',
        userAgent: (self.navigator && self.navigator.userAgent) || '',
        hardwareConcurrency: (self.navigator && self.navigator.hardwareConcurrency) || '',
        languages: JSON.stringify((self.navigator && self.navigator.languages) || []),
        cdpAutomation: String(cdp)
      });
    };
  `;

  let url = null;
  let worker = null;
  try {
    if (typeof Worker !== "function" || typeof Blob !== "function") return;

    const blob = new Blob([source], { type: "application/javascript" });
    url = URL.createObjectURL(blob);
    worker = new Worker(url);

    const data = await withTimeout(
      new Promise((resolve, reject) => {
        worker.onmessage = (event) => resolve(event.data);
        worker.onerror = reject;
        worker.postMessage("collect");
      }),
      WORKER_TIMEOUT_MS,
      null
    );

    if (!data) return;

    attrs.platformWebWorker = s(data.platform);
    attrs.userAgentWebWorker = s(data.userAgent);
    attrs.hardwareConcurrencyWebWorker = s(data.hardwareConcurrency);
    attrs.languagesWebWorker = s(data.languages);
    automation.cdpAutomationWebWorker = s(data.cdpAutomation);

    // Window realm vs worker realm. A disagreement means something is
    // rewriting navigator on the page.
    const uaMatches = s(data.userAgent) === s(navigator.userAgent);
    const coresMatch = s(data.hardwareConcurrency) === s(navigator.hardwareConcurrency);
    automation.workerRealmMismatch = s(!(uaMatches && coresMatch));
  } catch (e) {
    /* workers unavailable (strict CSP, for example) */
  } finally {
    try {
      if (worker) worker.terminate();
      if (url) URL.revokeObjectURL(url);
    } catch (e) {
      /* ignore */
    }
  }
}

/** Plugins, MIME types, languages. */
function collectBrowserInfo(attrs) {
  try {
    const plugins = [];
    for (let i = 0; i < navigator.plugins.length; i++) {
      const p = navigator.plugins[i];
      plugins.push(`${p.name}::${p.description}::${p.filename}`);
    }
    attrs.plugins = plugins.join(" - ");
  } catch (e) {
    attrs.plugins = "";
  }

  try {
    const mimes = [];
    for (let i = 0; i < navigator.mimeTypes.length; i++) {
      const m = navigator.mimeTypes[i];
      mimes.push(`${m.description}~~${m.type}~~${m.suffixes}`);
    }
    attrs.mimeTypes = mimes.join(",");
  } catch (e) {
    attrs.mimeTypes = "";
  }

  try {
    attrs.mainLanguage = s(navigator.language);
    attrs.languages = (navigator.languages || []).join(",");
  } catch (e) {
    /* ignore */
  }
}

/** Display capability, pointer/hover, media device counts. */
async function collectDeviceSensors(attrs) {
  try {
    attrs.colorDepth = s(window.screen.colorDepth);

    const gamuts = ["srgb", "p3", "rec2020"].filter(
      (g) => window.matchMedia(`(color-gamut: ${g})`).matches
    );
    attrs.colorGamut = gamuts.length ? gamuts.join(",") : "any";

    const pointers = ["none", "coarse", "fine"].filter(
      (p) => window.matchMedia(`(any-pointer: ${p})`).matches
    );
    attrs.anyPointer = pointers.length ? pointers.join(",") : "any";

    const hovers = ["none", "hover"].filter(
      (h) => window.matchMedia(`(any-hover: ${h})`).matches
    );
    attrs.anyHover = hovers.length ? hovers.join(",") : "any";

    attrs.screenTouchPoints = s(navigator.maxTouchPoints);

    // Capability check. The original registered a one-shot `devicemotion`
    // listener that could not possibly fire before the payload was sent, so
    // it reported "false" on every device including phones - and left a
    // listener attached to window in a SPA that never unmounts it.
    attrs.accelerometer = s(
      typeof window.DeviceMotionEvent !== "undefined" &&
        typeof window.DeviceMotionEvent.requestPermission !== "function"
        ? "available"
        : typeof window.DeviceMotionEvent !== "undefined"
        ? "permission-required"
        : "unavailable"
    );
  } catch (e) {
    /* ignore */
  }

  try {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      const devices = await withTimeout(
        navigator.mediaDevices.enumerateDevices(),
        GPU_TIMEOUT_MS,
        []
      );
      let mic = 0;
      let speaker = 0;
      let cam = 0;
      devices.forEach((d) => {
        if (d.kind === "audioinput") mic++;
        else if (d.kind === "audiooutput") speaker++;
        else if (d.kind === "videoinput") cam++;
      });
      attrs.numOfMicrophones = s(mic);
      attrs.numOfSpeaker = s(speaker);
      attrs.numOfWebcams = s(cam);
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * Installed font probe.
 *
 * Measures a reference string in each candidate font against three generic
 * families; a width that differs from the generic means the font resolved.
 * The probe span is positioned off-screen - in the original it was appended
 * inline at 72px, which flashed a line of "mmmmmmmmmmlli" into the page.
 */
function collectFonts(attrs) {
  const testFonts = [
    "Agency FB", "Calibri", "Century", "Century Gothic",
    "Franklin Gothic", "Haettenschweiler", "Leelawadee",
    "Lucida Bright", "Lucida Sans", "MS Outlook",
    "MS Reference Specialty", "MS UI Gothic",
    "MT Extra", "Marlett", "Microsoft Uighur",
    "Monotype Corsiva", "Pristina",
    "Segoe UI Light", "Univers CE 55 Medium",
  ];
  const baseFonts = ["monospace", "sans-serif", "serif"];

  let detector = null;
  try {
    detector = document.createElement("span");
    detector.setAttribute("aria-hidden", "true");
    detector.style.cssText =
      "position:absolute;left:-9999px;top:-9999px;visibility:hidden;" +
      "font-size:72px;line-height:normal;white-space:nowrap;";
    detector.textContent = "mmmmmmmmmmlli";
    document.body.appendChild(detector);

    const defaultWidths = {};
    baseFonts.forEach((base) => {
      detector.style.fontFamily = base;
      defaultWidths[base] = detector.getBoundingClientRect().width;
    });

    const detected = testFonts.filter((font) =>
      baseFonts.some((base) => {
        detector.style.fontFamily = `"${font}",${base}`;
        return detector.getBoundingClientRect().width !== defaultWidths[base];
      })
    );

    attrs.fontList = detected.join(",");
    attrs.fontCount = s(detected.length);
  } catch (e) {
    attrs.fontList = "";
  } finally {
    try {
      if (detector && detector.parentNode) detector.parentNode.removeChild(detector);
    } catch (e) {
      /* ignore */
    }
  }
}

/**
 * WebGL: driver strings, capability limits, extension list, and a hash of an
 * actual render.
 *
 * The renderer string ("ANGLE (NVIDIA, NVIDIA GeForce RTX 4070 ...)") is the
 * single highest entropy attribute available in a browser, which is exactly
 * why Safari and Firefox-with-RFP mask it. The render hash still separates
 * machines when the strings are masked, because rasterisation differs by GPU
 * and driver.
 */
function collectWebGL(attrs) {
  let canvas = null;
  try {
    canvas = document.createElement("canvas");
    canvas.width = 300;
    canvas.height = 150;

    const gl =
      canvas.getContext("webgl", { preserveDrawingBuffer: true }) ||
      canvas.getContext("experimental-webgl", { preserveDrawingBuffer: true });

    if (!gl) {
      attrs.webglSupported = "No";
      return;
    }
    attrs.webglSupported = "Yes";

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR);
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    const version = gl.getParameter(gl.VERSION);

    attrs.webglVendor = s(vendor);
    attrs.webglRenderer = s(renderer);
    attrs.webglVersion = s(version);
    attrs.webglMasked = s(!debugInfo);
    // Same values under the names the MVC reader uses.
    attrs.gpuVendor = s(vendor);
    attrs.gpuRenderer = s(renderer);

    attrs.webglMaxTextureSize = s(gl.getParameter(gl.MAX_TEXTURE_SIZE));
    attrs.webglMaxViewportDimensions = s(gl.getParameter(gl.MAX_VIEWPORT_DIMS));
    attrs.webglAliasedLineWidthRange = s(gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE));
    attrs.webglAliasedPointSizeRange = s(gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE));

    const extensions = gl.getSupportedExtensions() || [];
    attrs.webglExtensionsCount = s(extensions.length);
    attrs.webglExtensions = extensions.join(",");

    attrs.webglHash = fnv1a(
      [
        s(vendor),
        s(renderer),
        s(version),
        attrs.webglMaxTextureSize,
        attrs.webglMaxViewportDimensions,
        extensions.slice(0, 20).join(","),
      ].join("|")
    );

    // Render a fixed triangle and hash the pixels.
    gl.viewport(0, 0, 300, 150);
    gl.clearColor(0.1, 0.1, 0.3, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(
      vertexShader,
      "attribute vec4 aVertexPosition;void main(void){gl_Position=aVertexPosition;}"
    );
    gl.compileShader(vertexShader);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(
      fragmentShader,
      "precision mediump float;void main(void){gl_FragColor=vec4(0.8,0.3,0.2,1.0);}"
    );
    gl.compileShader(fragmentShader);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-0.5, -0.5, 0.5, -0.5, 0.0, 0.5]),
      gl.STATIC_DRAW
    );

    const position = gl.getAttribLocation(program, "aVertexPosition");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // Hashed here; the data URL itself is never transmitted.
    attrs.webglCanvasHash = fnv1a(canvas.toDataURL());
  } catch (e) {
    /* WebGL unavailable or context lost */
  }
}

/** 2D canvas text render hash - a different subsystem from WebGL. */
function collectCanvas2d(attrs) {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 300;
    canvas.height = 150;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ccf";
    ctx.fillRect(10, 10, 280, 130);
    ctx.fillStyle = "#000";
    ctx.font = "20px Arial";
    ctx.fillText("WebGL Fingerprint Test", 20, 80);
    ctx.textBaseline = "alphabetic";
    ctx.font = "16px 'Arial'";
    ctx.fillText("RPost \u{1F510} RDocs", 20, 120);
    attrs.canvas2dHash = fnv1a(canvas.toDataURL());
  } catch (e) {
    /* ignore */
  }
}

/**
 * WebGPU adapter.
 *
 * `adapter.limits` is a platform object whose values live on the prototype,
 * so the original's JSON.stringify(adapter.limits) produced "{}". Enumerated
 * explicitly here. Adapter identity also moved from adapter.<field> to
 * adapter.info / requestAdapterInfo() - both spellings handled.
 */
async function collectWebGPU(attrs) {
  try {
    if (!navigator.gpu) return;

    const adapter = await withTimeout(navigator.gpu.requestAdapter(), GPU_TIMEOUT_MS, null);
    if (!adapter) return;

    let info = adapter.info || null;
    if (!info && typeof adapter.requestAdapterInfo === "function") {
      info = await withTimeout(adapter.requestAdapterInfo(), GPU_TIMEOUT_MS, null);
    }

    attrs.gpuAdapterInfo = JSON.stringify({
      vendor: s(info && info.vendor, "unknown"),
      architecture: s(info && info.architecture, "unknown"),
      device: s(info && info.device, "unknown"),
      description: s(info && info.description, "unknown"),
    });

    if (adapter.limits) {
      const limits = {};
      for (const key in adapter.limits) {
        const value = adapter.limits[key];
        if (typeof value === "number" || typeof value === "string") limits[key] = value;
      }
      attrs.gpuDeviceLimits = JSON.stringify(limits);
    }
  } catch (e) {
    /* WebGPU unavailable */
  }
}

/** CPU cores and memory bucket. */
function collectHardware(attrs) {
  try {
    attrs.cpuCores = s(navigator.hardwareConcurrency);
    attrs.deviceMemory = navigator.deviceMemory ? `${navigator.deviceMemory} GB` : "";
  } catch (e) {
    /* ignore */
  }
}

/** Physical keyboard layout - reflects the OS keyboard, not the UI language. */
async function collectKeyboard(attrs) {
  try {
    if (!navigator.keyboard || typeof navigator.keyboard.getLayoutMap !== "function") {
      attrs.keyboardlayoutSize = "Not supported";
      attrs.keyboardlayoutMap = "Keyboard API not supported";
      return;
    }
    const map = await withTimeout(navigator.keyboard.getLayoutMap(), GPU_TIMEOUT_MS, null);
    if (!map) {
      attrs.keyboardlayoutSize = "Not supported";
      return;
    }
    const entries = [];
    map.forEach((value, key) => entries.push(`${key},${value}`));
    attrs.keyboardlayoutSize = s(entries.length);
    attrs.keyboardlayoutMap = entries.join(" ");
  } catch (e) {
    attrs.keyboardlayoutSize = "Not supported";
  }
}

/** Locale, timezone. */
function collectLocale(attrs) {
  try {
    attrs.localeLanguageDatetimeFormat = s(
      new Intl.RelativeTimeFormat().resolvedOptions().locale
    );
  } catch (e) {
    /* ignore */
  }
  try {
    attrs.timezoneName = s(Intl.DateTimeFormat().resolvedOptions().timeZone);
    attrs.timezoneOffset = s(new Date().getTimezoneOffset() * -1);
  } catch (e) {
    /* ignore */
  }
}

/** Mobile vs desktop, per client hints with a User-Agent fallback. */
function collectMobile(attrs, notes) {
  try {
    const isMobile = navigator.userAgentData ? navigator.userAgentData.mobile : null;
    const ua = navigator.userAgent || "";

    attrs.isMobile = s(isMobile);
    attrs.mobileUserAgent = s(ua);

    let explanation;
    if (isMobile === true) {
      explanation = "Browser marked this as a MOBILE device based on User-Agent Client Hints.";
    } else if (isMobile === false) {
      explanation = "Browser marked this as a DESKTOP device based on User-Agent Client Hints.";
    } else {
      explanation = "navigator.userAgentData not supported in this browser.";
    }

    explanation += /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
      ? " User-Agent string also looks like a mobile device."
      : " User-Agent string looks like a desktop device.";

    notes.mobile = explanation;
  } catch (e) {
    /* ignore */
  }
}

function modelFromUA(ua) {
  const android = ua.match(/Android.*?;\s*(.*?)\sBuild/i);
  if (android) return android[1];
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  return "Unknown";
}

function platformFromUA(ua) {
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Mac OS X/i.test(ua)) return "macOS";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
}

function platformVersionFromUA(ua) {
  const android = ua.match(/Android\s+([\d.]+)/i);
  if (android) return android[1];
  const ios = ua.match(/OS\s+([\d_]+)/i);
  if (ios) return ios[1].replace(/_/g, ".");
  const win = ua.match(/Windows NT\s+([\d.]+)/i);
  if (win) return win[1];
  return "Unknown";
}

function architectureFromUA(ua) {
  if (/arm64|aarch64/i.test(ua)) return "arm64";
  if (/x86_64|Win64|WOW64/i.test(ua)) return "x64";
  if (/i686|x86/i.test(ua)) return "x86";
  return "Unknown";
}

function bitnessFromUA(ua) {
  if (/64/i.test(ua)) return "64";
  if (/32/i.test(ua)) return "32";
  return "Unknown";
}

/**
 * Device model and platform.
 *
 * The original shadowed its own `model` parameter with `let model =
 * values.model`, so every `model.deviceModel = ...` assignment landed on a
 * string primitive and was silently discarded - these fields never reached
 * the server.
 */
async function collectDeviceIdentity(attrs, notes) {
  const ua = navigator.userAgent || "";
  attrs.deviceModelUserAgent = s(ua);
  attrs.deviceInfoUserAgent = s(ua);

  let values = null;
  try {
    if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
      values = await withTimeout(
        navigator.userAgentData.getHighEntropyValues([
          "model",
          "mobile",
          "platform",
          "platformVersion",
          "architecture",
          "bitness",
        ]),
        GPU_TIMEOUT_MS,
        null
      );
    }
  } catch (e) {
    values = null;
  }

  if (values && values.model) {
    attrs.deviceModel = s(values.model);
    notes.deviceModel = "Model received from userAgentData Client Hints.";
  } else {
    attrs.deviceModel = modelFromUA(ua);
    notes.deviceModel = values
      ? "Model not provided by browser, taken from User-Agent."
      : "Browser does not support userAgentData. Using User-Agent.";
  }

  attrs.deviceInfoPlatform = s(values && values.platform, "") || platformFromUA(ua);
  attrs.deviceInfoPlatformVersion =
    s(values && values.platformVersion, "") || platformVersionFromUA(ua);
  attrs.deviceInfoArchitecture = s(values && values.architecture, "") || architectureFromUA(ua);
  attrs.deviceInfoBitness = s(values && values.bitness, "") || bitnessFromUA(ua);
  notes.deviceInfo = values
    ? "Fetched from userAgentData, falling back to the User-Agent string per field."
    : "userAgentData not supported. All values extracted from the User-Agent string.";
}

/**
 * Screen geometry.
 *
 * Read once. The original attached a `resize` listener that was never
 * removed, which leaks on every mount in a SPA - and innerResolution is
 * excluded from the visitorId anyway, precisely because it changes when the
 * reader resizes the window.
 */
function collectScreen(attrs) {
  try {
    attrs.screenResolution = `${window.screen.width}x${window.screen.height} pixels`;
    attrs.availableScreenResolution = `${window.screen.availWidth}x${window.screen.availHeight} pixels`;
    attrs.innerResolution = `${window.innerWidth}x${window.innerHeight} pixels`;
    attrs.devicePixelRatio = s(window.devicePixelRatio);
  } catch (e) {
    /* ignore */
  }
}

/**
 * Installed speech synthesis voices - a good proxy for OS and installed
 * language packs.
 *
 * Chrome returns [] on the first getVoices() call and fills the list
 * asynchronously. The original awaited a promise that only resolved from
 * inside `processVoices`, so on a browser that never fires
 * `onvoiceschanged` the await never returned and the whole collector hung
 * forever. Bounded here.
 */
async function collectVoices(attrs) {
  try {
    if (typeof window.speechSynthesis === "undefined") return;

    const voices = await withTimeout(
      new Promise((resolve) => {
        const immediate = window.speechSynthesis.getVoices();
        if (immediate && immediate.length) {
          resolve(immediate);
          return;
        }
        window.speechSynthesis.onvoiceschanged = () => {
          const later = window.speechSynthesis.getVoices();
          if (later && later.length) resolve(later);
        };
      }),
      VOICES_TIMEOUT_MS,
      []
    );

    try {
      window.speechSynthesis.onvoiceschanged = null;
    } catch (e) {
      /* ignore */
    }

    attrs.speechVoiceCount = s(voices.length);
    // Names + languages rather than the full JSON objects: same
    // discriminating power, a fraction of the bytes.
    attrs.speechVoiceDetails = voices
      .map((v) => `${v.name}~${v.lang}~${v.localService ? "1" : "0"}`)
      .join("|");
    attrs.speechVoiceHash = fnv1a(
      voices
        .map((v) => `${v.name}~${v.lang}`)
        .sort()
        .join("|")
    );
  } catch (e) {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ */
/* public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Runs every probe and returns the fingerprint.
 *
 * Independent probes run concurrently, so wall-clock cost is roughly the
 * slowest probe (the offline audio render) rather than their sum.
 *
 * @returns {Promise<object>} never rejects
 */
export async function collectDeviceFingerprint() {
  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const attributes = {};
  const notes = {};
  const automation = {};

  try {
    // Synchronous probes first - they cannot fail the batch.
    collectCodecs(attributes);
    collectAutomation(automation);
    collectBrowserInfo(attributes);
    collectFonts(attributes);
    collectWebGL(attributes);
    collectCanvas2d(attributes);
    collectHardware(attributes);
    collectLocale(attributes);
    collectMobile(attributes, notes);
    collectScreen(attributes);

    await Promise.all([
      collectAudio(attributes),
      collectClientHints(attributes, notes),
      collectWorker(attributes, automation),
      collectDeviceSensors(attributes),
      collectWebGPU(attributes),
      collectKeyboard(attributes),
      collectDeviceIdentity(attributes, notes),
      collectVoices(attributes),
    ]);
  } catch (e) {
    /* a probe escaped its own guard - keep whatever was collected */
  }

  // visitorId over the stable subset only, in a fixed key order so the same
  // machine always hashes to the same value.
  const stablePairs = STABLE_KEYS.map((key) => `${key}=${attributes[key] || ""}`);
  const present = STABLE_KEYS.filter(
    (key) => attributes[key] !== undefined && attributes[key] !== ""
  ).length;

  const visitorId = await sha256Hex(`${FP_VERSION}|${stablePairs.join("|")}`);
  const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();

  return {
    visitorId,
    version: FP_VERSION,
    collectedOn: new Date().toISOString(),
    collectionMs: Math.round(endedAt - startedAt),
    // Share of stable attributes actually obtained. A hardened browser
    // (Safari, Firefox with resistFingerprinting, Tor) scores low, which is
    // the signal that this id should be trusted less.
    confidence: Number((present / STABLE_KEYS.length).toFixed(3)),
    automation,
    attributes,
    notes,
  };
}

export default collectDeviceFingerprint;
